<template>
  <div>
    <FilePond
      class='filepond-theme-sami'
      :disabled="isDisabled"
      ref="pond"
      labelIdle="+ Select File"
      labelTapToUndo=""
      labelTapToCancel=""
      labelTapToRetry=""
      :maxParallelUploads="1"
      :allowMultiple="config.multiple"
      :allowBrowse="true"
      :allowDrop="false"
      :allowRevert="false"
      :acceptedFileTypes="info.acceptedMimeTypesValues"
      :files="files"
      :server="serverConfig"
      :maxFiles="maxFiles"
      :minFileSize="config.minSize"
      :maxFileSize="config.maxSize"
      @init="handleFilePondInit"
      @activatefile="downloadFile"
      :beforeRemoveFile="beforeRemoveHook"/>

    <slot name="info" v-bind:config="info"></slot>

    <Modal :show="isModalShow" @close="onCloseModal">
      <div class="text-center mb-4">
        <font-awesome-icon class="text-warning" :icon="['fas', 'trash-alt']" size="4x" />
        <h1 class="text-info text-center my-4">Delete a file</h1>
        <p class="lead mb-5">Hi! Do you want to permanently delete this file?</p>
        <button
          class="btn btn-wide btn-light btn--cancel mr-1 font-weight-bold"
          :disabled="isDeleting"
          @click="onCloseModalCustom">No, thanks</button>
        <button
          class="btn btn-wide btn-info ml-1 font-weight-bold"
          @click="deleteFile(selectedFile)"
          :disabled="isDeleting">
          <span v-if="!isDeleting">Yes, please delete it</span>
          <span v-if="isDeleting">
            <font-awesome-icon class="text-white" :icon="['fas', 'spinner']" spin />
          </span>
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import Modal from '@/components/_shared/Modal.vue'
import ModalMixin from '@/mixins/modal'

import 'filepond/dist/filepond.min.css'

import apiFiles from '@/api/files'

import VueFilePond from 'vue-filepond'

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'

const FilePond = VueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImageValidateSize,
  FilePondPluginFileValidateSize
)

const baseURL = process.env.VUE_APP_END_POINT_FILES

export default {
  name: 'FileUpload',
  components: { FilePond, Modal },
  mixins: [ ModalMixin ],
  data () {
    return {
      files: [],
      isDeleting: false,
      selectedFile: null,
      serverConfig: {
        url: baseURL,
        process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
          const fileName = file.name
          const mimeType = file.type
          const pathfile = `${this.config.path}/${fileName}`
          const userId = this.config.userId
          const opportunityId = this.config.uuid
          const requestPayload = {
            pathfile,
            data: { userId, opportunityId }
          }

          // START: Request requestUpload
          apiFiles
            .requestUpload(requestPayload)
            .then((response) => {
              if (response.data && response.data.status) {
                const reader = new FileReader()
                reader.readAsArrayBuffer(file)

                reader.onerror = (error) => {
                  console.log('Error:', error)
                }

                reader.onload = () => {
                  const formData = reader.result
                  const linkToUpload = response.data.data.link_to_upload
                  const uploadPayload = { linkToUpload, mimeType, formData, progress }

                  // START: Request uploadFile
                  apiFiles
                    .uploadFile(uploadPayload)
                    .then((response) => {
                      if (response.status === 200) {
                        const confirmPayload = { pathfile }

                        // START: Request confirmUpload
                        apiFiles
                          .confirmUpload(confirmPayload)
                          .then((response) => {
                            load(pathfile)
                            this.$notification.success(response.data.message)
                            this.handleFilePondInit()
                          })
                          .catch(err => {
                            console.log(err)
                            error(err)
                          })
                        // END: Request confirmUpload
                      }
                    })
                    .catch(err => {
                      console.log(err)
                      error(err)
                    })
                  // END: Request uploadFile
                }
              } else {
                this.$notification.warning(response.data.message)
                this.handleFilePondInit()
                abort()
              }
              // this.files = response.data.data
              // this.$refs.pond.getFiles()
            })
            .catch(err => {
              console.log(err)
              error(err)
            })
          // END: Request requestUpload
        },
        remove: (source, load, error) => {
          const path = this.config.path
          const fileName = source.split('/').slice(-1)
          const pathfile = `${path}/${fileName}`
          const deleteUploadPayload = { pathfile }
          // START: Request deleteUpload
          apiFiles
            .deleteUpload(deleteUploadPayload)
            .then((response) => {
              if (response.data.status) {
                load()
                this.$notification.success(response.data.message)
              }
              if (error) {
                error()
              }
            })
            .catch(err => {
              console.log(err)
              error(err)
            })
          // END: Request deleteUpload
        },
        revert: null
      }
    }
  },
  props: {
    multiple: {
      type: Boolean,
      default: true
    },
    maxFiles: {
      type: Number,
      default: 50
    },
    config: {
      type: Object,
      required: true
    },
    isDisabled: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    info () {
      return {
        maxSize: this.config.maxSize,
        acceptedMimeTypesValues: this.config.acceptedMimeTypes.map(m => m.mimeType).join(', '),
        acceptedMimeTypesLabels: this.config.acceptedMimeTypes.map(m => m.ext).join(', ')
      }
    }
  },
  methods: {
    deleteFile () {
      this.isDeleting = true
      if (this.selectedFile) {
        this.$refs.pond.removeFile(this.selectedFile)
        this.selectedFile = null
        this.isDeleting = false
        this.onCloseModal()
      }
      return true
    },
    beforeRemoveHook (item) {
      this.selectedFile = item.id
      this.onShowModal()
      return false
    },
    onCloseModalCustom () {
      this.selectedFile = null
      this.onCloseModal()
    },
    handleFilePondInit () {
      const payload = { path: this.config.path }
      apiFiles
        .listUpload(payload)
        .then((response) => {
          this.files = response.data.data
            .map(x => {
              return {
                source: x[1],
                options: {
                  type: 'local',
                  file: {
                    name: x[0],
                    size: 1000
                  }
                }
              }
            })
          this.files.reverse()
          this.$refs.pond.getFiles()
        })
        .catch(error => {
          console.log('Handle FilePond Init', error)
        })
    },
    downloadFile (file) {
      let fileLink = document.createElement('a')
      let fileName = file.filename
      fileLink.style.display = 'none'
      fileLink.href = file.source
      fileLink.download = fileName
      fileLink.target = '_blank'
      document.body.appendChild(fileLink)
      fileLink.click()
      fileLink.remove()
      return false
    }
  }
}
</script>

<style>
  .filepond--root {
    font-size: 1.1rem;
  }
  .filepond--drop-label {
    color: #00a7cb;
  }
  .filepond--file-info {
    transform: translate3d(31px, 4px, 0px) !important;
  }
  .filepond--file-info-sub {
    color: #64605e;
  }
  .filepond--file-status {
    transform: translate3d(0px, 4px, 0px) !important;
  }
  .filepond--action-process-item,
  .filepond--action-revert-item-processing {
    display: none;
  }
  .filepond--file-wrapper {
    cursor: pointer;
  }
</style>
